import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Calendar, User, ArrowRight } from 'lucide-react';
import api from '@/lib/api';
import { formatDate } from '@/lib/format';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';

// very lightweight markdown rendering for headings, bold, lists
function renderMarkdown(md) {
  if (!md) return '';
  const lines = md.split('\n');
  const html = [];
  let inUL = false;
  let inOL = false;
  const closeLists = () => { if (inUL) { html.push('</ul>'); inUL = false; } if (inOL) { html.push('</ol>'); inOL = false; } };

  for (let raw of lines) {
    const line = raw.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
    if (!line.trim()) { closeLists(); html.push(''); continue; }
    if (line.startsWith('## ')) { closeLists(); html.push(`<h2>${line.slice(3)}</h2>`); continue; }
    if (line.startsWith('### ')) { closeLists(); html.push(`<h3>${line.slice(4)}</h3>`); continue; }
    if (/^\s*-\s+/.test(line)) {
      if (!inUL) { closeLists(); html.push('<ul>'); inUL = true; }
      html.push(`<li>${line.replace(/^\s*-\s+/, '')}</li>`);
      continue;
    }
    if (/^\s*\d+\.\s+/.test(line)) {
      if (!inOL) { closeLists(); html.push('<ol>'); inOL = true; }
      html.push(`<li>${line.replace(/^\s*\d+\.\s+/, '')}</li>`);
      continue;
    }
    closeLists();
    html.push(`<p>${line}</p>`);
  }
  closeLists();
  return html.join('\n');
}

export default function BlogDetailPage() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [related, setRelated] = useState([]);

  useEffect(() => {
    setLoading(true);
    api.get(`/blog/${slug}`)
      .then((res) => { setPost(res.data); document.title = `${res.data.title} | Cespedis`; })
      .catch(() => setPost(null))
      .finally(() => setLoading(false));
    api.get('/blog').then((res) => setRelated((res.data || []).slice(0, 3))).catch(() => {});
  }, [slug]);

  if (loading) return <div className="pt-32 pb-20 mx-auto max-w-3xl px-4 animate-pulse"><div className="h-72 w-full rounded-2xl bg-slate-100" /><div className="mt-6 h-8 w-2/3 rounded bg-slate-100" /></div>;
  if (!post) return <div className="pt-32 pb-20 mx-auto max-w-3xl px-4 text-center"><h1 className="font-display text-3xl font-semibold text-[#144726]">Article not found</h1><Link to="/blog"><Button className="mt-6 bg-[#144726] text-white"><ArrowLeft className="mr-2 h-4 w-4" /> Back to blog</Button></Link></div>;

  return (
    <div data-testid="blog-detail-page">
      <article>
        <section className="relative isolate overflow-hidden bg-[#144726] pt-32 pb-20 md:pt-40 md:pb-28">
          <img src={post.cover_image} alt="" className="absolute inset-0 -z-10 h-full w-full object-cover opacity-25" />
          <div className="absolute inset-0 -z-10 bg-gradient-to-b from-[#144726] via-[#144726]/85 to-[#144726]" />
          <div className="mx-auto w-full max-w-3xl px-4 sm:px-6 lg:px-8">
            <Link to="/blog" className="inline-flex items-center gap-2 text-sm font-semibold text-white/80 hover:text-white"><ArrowLeft className="h-4 w-4" /> Back to insights</Link>
            <div className="mt-4 flex flex-wrap items-center gap-2">{post.tags?.map((t) => <Badge key={t} className="rounded-md bg-[#C6A35B] text-[#144726] border-0">{t}</Badge>)}</div>
            <h1 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-semibold text-white tracking-tight">{post.title}</h1>
            <div className="mt-5 flex flex-wrap items-center gap-4 text-sm text-white/75">
              <span className="inline-flex items-center gap-1"><Calendar className="h-4 w-4 text-[#C6A35B]" /> {formatDate(post.published_at)}</span>
              <span className="inline-flex items-center gap-1"><User className="h-4 w-4 text-[#C6A35B]" /> {post.author}</span>
            </div>
          </div>
        </section>

        <section className="py-14 md:py-20 bg-white">
          <div className="mx-auto w-full max-w-3xl px-4 sm:px-6 lg:px-8">
            <div className="overflow-hidden rounded-2xl border border-slate-200"><img src={post.cover_image} alt={post.title} className="h-[280px] w-full object-cover md:h-[420px]" /></div>
            <div className="prose-article mt-10" dangerouslySetInnerHTML={{ __html: renderMarkdown(post.content) }} />
          </div>
        </section>
      </article>

      <section className="bg-slate-50 py-16 md:py-20">
        <div className="mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8">
          <h3 className="font-display text-2xl md:text-3xl font-semibold text-[#144726]">Continue reading</h3>
          <div className="mt-8 grid grid-cols-1 gap-6 md:grid-cols-3">
            {related.filter((r) => r.id !== post.id).slice(0, 3).map((p) => (
              <Link key={p.id} to={`/blog/${p.slug || p.id}`} className="group flex h-full flex-col overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm hover:shadow-md transition-shadow">
                <div className="aspect-[16/10] overflow-hidden"><img src={p.cover_image} alt={p.title} className="h-full w-full object-cover transition-transform duration-[700ms] group-hover:scale-105" /></div>
                <div className="flex flex-1 flex-col p-6">
                  <h4 className="font-display text-lg font-semibold text-[#144726] line-clamp-2">{p.title}</h4>
                  <p className="mt-2 text-sm text-slate-600 line-clamp-3">{p.excerpt}</p>
                  <p className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-[#144726] group-hover:text-[#C6A35B]">Read <ArrowRight className="h-4 w-4" /></p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
